<div class="entity-lookup-dialog time-off-policies-dialog">
  <div class="dialog-header">
    <h2 class="dialog-title">Time-Off Policies</h2>
    <button
      mat-icon-button
      class="close-button"
      tabindex="-1"
      aria-label="Close"
      (click)="cancel()"
    >
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <mat-dialog-content class="policies-content" #policiesContent>
    <!-- Named so it reads as the peer of Practice Area Overrides below, rather than as
         unlabelled preamble the overrides section then appears to hang off. -->
    <div class="office-heading">
      <h3 class="office-heading__title">Office</h3>
      <p class="policies-intro">
        Set the default time-off allotments for
        <strong>{{ data.officeName }}</strong
        >. These apply to every resource in the office unless a practice area
        below overrides them, or a resource has an override of their own.
      </p>
    </div>

    @for (policy of policies; track policy.typeCode) {
      <div class="policy-group">
        <h3 class="policy-group__title">{{ policy.typeName }}</h3>
        <div class="policy-fields">
          <!-- These feed canSave exactly as the override rows below do, so they need the same
               error display: step="1" is only a browser hint, and a decimal or negative value
               would otherwise disable Save with nothing on screen to account for it. -->
          <mat-form-field appearance="outline" class="policy-field">
            <mat-label>Default days</mat-label>
            <input
              matInput
              type="number"
              min="0"
              step="1"
              required
              [(ngModel)]="policy.defaultDays"
              [errorStateMatcher]="daysErrorMatcher"
            />
            <mat-error>Enter a whole number of days, 0 or more.</mat-error>
          </mat-form-field>
          <mat-form-field appearance="outline" class="policy-field">
            <mat-label>Max carryover</mat-label>
            <input
              matInput
              type="number"
              min="0"
              step="1"
              placeholder="Unlimited"
              [(ngModel)]="policy.maxCarryoverDays"
              [errorStateMatcher]="carryoverErrorMatcher"
            />
            <mat-error>Enter a whole number of days, or leave blank.</mat-error>
          </mat-form-field>
        </div>
      </div>
    }

    <!-- Per-practice-area Vacation overrides. Rows live in the component's working
         array, so filtering or paging a row out of view never discards an edit. -->
    <section class="overrides-section">
      <!-- Collapsed by default so the dialog opens without a scrollbar, mirroring the
           same section on the office details card. The count stays visible while
           collapsed, so an office with overrides still advertises them. -->
      <button
        type="button"
        class="overrides-toggle"
        (click)="toggleOverridesExpanded()"
        [attr.aria-expanded]="overridesExpanded"
      >
        <mat-icon
          class="expansion-indicator"
          [style.transform]="
            overridesExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
          "
          >expand_more</mat-icon
        >
        <span class="overrides-toggle__title">Practice Area Overrides</span>
        <span class="overrides-toggle__count"
          >{{ enabledOverrideCount }} active</span
        >
      </button>

      @if (overridesExpanded) {
        <p class="overrides-hint">
          Override the <strong>Vacation</strong> policy for resources whose
          primary practice area is switched on below. Practice areas left off
          follow the office default. Sick is not overridable.
        </p>

        <mat-form-field
          appearance="outline"
          class="overrides-search"
          subscriptSizing="dynamic"
        >
          <input
            matInput
            [ngModel]="overrideFilter"
            (ngModelChange)="onOverrideFilterChange($event)"
            placeholder="Filter practice areas"
            aria-label="Filter practice areas"
          />
          <mat-icon
            matSuffix
            [style.cursor]="overrideFilter ? 'pointer' : 'default'"
            (click)="clearOverrideFilter()"
            >{{ overrideFilter ? "close" : "search" }}</mat-icon
          >
        </mat-form-field>

        <div class="overrides-table-wrap">
          <table
            mat-table
            [dataSource]="overridesDataSource"
            class="overrides-table"
          >
            <!-- Name + code -->
            <ng-container matColumnDef="practiceArea">
              <th mat-header-cell *matHeaderCellDef>Practice area</th>
              <td mat-cell *matCellDef="let row">
                <div class="pa-item__name" [matTooltip]="row.practiceAreaName">
                  {{ row.practiceAreaName }}
                </div>
                <div class="pa-item__meta">{{ row.practiceAreaCode }}</div>
              </td>
            </ng-container>

            <!-- On/off -->
            <ng-container matColumnDef="override">
              <th mat-header-cell *matHeaderCellDef class="col-override">
                Override
              </th>
              <td mat-cell *matCellDef="let row" class="col-override">
                <mat-slide-toggle
                  [ngModel]="row.enabled"
                  (ngModelChange)="toggleOverride(row, $event)"
                  color="primary"
                  class="toggle-success"
                  [hideIcon]="true"
                  [attr.aria-label]="
                    'Override Vacation policy for ' + row.practiceAreaName
                  "
                ></mat-slide-toggle>
              </td>
            </ng-container>

            <!-- Vacation days (only editable while the override is on) -->
            <ng-container matColumnDef="defaultDays">
              <th mat-header-cell *matHeaderCellDef class="col-days">
                Vacation days
              </th>
              <td mat-cell *matCellDef="let row" class="col-days">
                @if (row.enabled) {
                  <mat-form-field
                    appearance="outline"
                    class="override-field"
                    subscriptSizing="dynamic"
                  >
                    <input
                      matInput
                      type="number"
                      min="0"
                      step="1"
                      required
                      [(ngModel)]="row.defaultDays"
                      [errorStateMatcher]="daysErrorMatcher"
                      [attr.aria-label]="
                        'Vacation days for ' + row.practiceAreaName
                      "
                    />
                    <mat-error>Whole days, 0+</mat-error>
                  </mat-form-field>
                } @else {
                  <span class="overrides-follow">Follows office default</span>
                }
              </td>
            </ng-container>

            <!-- Max carryover (blank = no cap, same convention as the office fields above) -->
            <ng-container matColumnDef="maxCarryoverDays">
              <th mat-header-cell *matHeaderCellDef class="col-days">
                Max carryover
              </th>
              <td mat-cell *matCellDef="let row" class="col-days">
                @if (row.enabled) {
                  <mat-form-field
                    appearance="outline"
                    class="override-field"
                    subscriptSizing="dynamic"
                  >
                    <input
                      matInput
                      type="number"
                      min="0"
                      step="1"
                      placeholder="Unlimited"
                      [(ngModel)]="row.maxCarryoverDays"
                      [errorStateMatcher]="carryoverErrorMatcher"
                      [attr.aria-label]="
                        'Max carryover for ' + row.practiceAreaName
                      "
                    />
                    <mat-error>Whole days, 0+</mat-error>
                  </mat-form-field>
                }
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="overrideColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: overrideColumns"></tr>

            <tr class="mat-row" *matNoDataRow>
              <td
                class="mat-cell overrides-no-data"
                [attr.colspan]="overrideColumns.length"
              >
                <!-- Also shown when there are no practice areas at all, so only blame the
                   filter when there is something to filter. -->
                <div class="overrides-empty">
                  @if (overrides.length === 0) {
                    No practice areas are available.
                  } @else {
                    No practice areas match your filter.
                  }
                </div>
              </td>
            </tr>
          </table>

          <mat-paginator
            class="overrides-paginator"
            [pageSize]="overridePageSize"
            [pageSizeOptions]="overridePageSizeOptions"
            showFirstLastButtons
            aria-label="Select page of practice areas"
          ></mat-paginator>
        </div>
      }
    </section>
  </mat-dialog-content>

  <mat-dialog-actions class="dialog-actions">
    <!-- Save considers every override row, including any the filter or the paginator is
         hiding, so name the row that is blocking it — otherwise the button is simply dead
         with nothing on screen to account for it. -->
    @if (invalidOverrideRows.length > 0) {
      <p class="overrides-invalid" role="alert">
        <mat-icon class="overrides-invalid__icon">error_outline</mat-icon>
        <span>
          @if (invalidOverrideRows.length === 1) {
            <strong>{{ firstInvalidOverrideLabel }}</strong> needs a whole
            number of days.
          } @else {
            <strong>{{ invalidOverrideRows.length }} practice areas</strong>
            need a whole number of days, starting with
            <strong>{{ firstInvalidOverrideLabel }}</strong
            >.
          }
          <button
            type="button"
            class="overrides-invalid__link"
            (click)="showInvalidOverrides()"
          >
            Show me
          </button>
        </span>
      </p>
    }
    <button
      mat-stroked-button
      type="button"
      class="cancel-button"
      (click)="cancel()"
    >
      Cancel
    </button>
    <button
      mat-flat-button
      type="button"
      class="save-button"
      [disabled]="!canSave"
      (click)="save()"
    >
      Save
    </button>
  </mat-dialog-actions>
</div>
